import { useContext, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import TabGroup from '../components/ui/TabGroup';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import TimelineIcon from '@mui/icons-material/TimelineOutlined';
import { AuthContext } from '../auth/AuthContext';
import EmailSettingsPanel from '../components/EmailSettingsPanel';
import DepartmentSettingsPanel from '../components/DepartmentSettingsPanel';
import TrackerStagesPanel from '../components/TrackerStagesPanel';
import RolePermissionsPage from './RolePermissionsPage';
import { isAdminRole } from '../constants/departments';

const TAB_KEYS = ['email', 'permissions', 'departments', 'tracker'];

export default function SettingsPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const permissions = useMemo(() => user?.permissions || [], [user]);
  const canEditEmail = useMemo(
    () =>
      ['superadmin', 'admin'].includes(String(user?.role || '').toLowerCase()) ||
      permissions.includes('settings.email.edit'),
    [user, permissions]
  );
  const canEditMatrix = useMemo(
    () => !!user?.can_edit_matrix || permissions.includes('permissions.matrix.edit'),
    [user, permissions]
  );
  const isPlatformAdmin = isAdminRole(user?.role);

  // Resolve which tabs the current user can actually open. Email-only users still
  // get a usable page; permissions-only users skip straight past the email tab.
  const availableTabs = useMemo(() => {
    const tabs = [];
    if (canEditEmail) tabs.push({ key: 'email', label: 'Email Settings', Icon: EmailIcon });
    if (canEditMatrix) tabs.push({ key: 'permissions', label: 'Role Permissions', Icon: LockIcon });
    if (isPlatformAdmin) tabs.push({ key: 'departments', label: 'Departments', Icon: BusinessIcon });
    if (isPlatformAdmin) tabs.push({ key: 'tracker', label: 'Tracker Stages', Icon: TimelineIcon });
    return tabs;
  }, [canEditEmail, canEditMatrix, isPlatformAdmin]);

  const requestedTab = searchParams.get('tab');
  const activeTab =
    availableTabs.find((t) => t.key === requestedTab)?.key ||
    availableTabs[0]?.key ||
    'email';

  useEffect(() => {
    if (availableTabs.length === 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [availableTabs, navigate]);

  const handleChange = (_e, value) => {
    setSearchParams({ tab: value });
  };

  if (availableTabs.length === 0) return null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Manage email notifications and role-based access in one place.
      </Typography>

      {/* Replaced MUI's underline Tabs with the app's pill group — cheaper and
          more faithful than overriding MUI internals to match. */}
      <Box sx={{ mb: 2 }}>
        <TabGroup
          tabs={availableTabs.map((t) => ({ key: t.key, label: t.label }))}
          value={activeTab}
          onChange={(key) => handleChange(null, key)}
        />
      </Box>

      {activeTab === 'email' && canEditEmail && <EmailSettingsPanel />}
      {activeTab === 'permissions' && canEditMatrix && <RolePermissionsPage />}
      {activeTab === 'departments' && isPlatformAdmin && <DepartmentSettingsPanel />}
      {activeTab === 'tracker' && isPlatformAdmin && <TrackerStagesPanel />}
    </Box>
  );
}

export { TAB_KEYS };
